import { useState } from 'react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScrollText, Plus, Pencil, Trash2, History, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../EmptyState';
import Modal from '../Modal';
import ConfirmModal from '../ConfirmModal';
import ReasonModal from '../sales/ReasonModal';
import CreateContractFromQuoteModal, { type ContractSourceQuote } from '../sales/CreateContractFromQuoteModal';
import { HubPanel, HubSurface, HubTable } from './HubPanel';
import {
  ClassificationBadge, ContractStatusBadge, InlineError, Spinner, apiErrorMessage, useDateFormatter,
} from '../sales/shared';
import { CONTRACT_TRANSITIONS } from '../../types';
import type { Contract, ContractDetail, ContractStatus } from '../../types';
import EntityPicker from '../ui/EntityPicker';
import DateInput from '../ui/DateInput';

/**
 * The customer's contracts (decision 51) — a distinct entity from quotes and
 * from service-contract engagements. Terms are free text entered by the user;
 * no legal wording or templates are generated.
 */
export default function ContractsTab({
  orgId,
  canWrite,
  acceptedQuotes,
  openContractId,
  onOpenContract,
}: {
  orgId: string;
  canWrite: boolean;
  /** Accepted quotes that do not have a contract yet (from the hub summary). */
  acceptedQuotes: ContractSourceQuote[];
  /** Contract whose detail is open (kept in the URL by the page). */
  openContractId: string | null;
  onOpenContract: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fmtDate = useDateFormatter();
  const [showCreate, setShowCreate] = useState(false);
  const [showFromQuote, setShowFromQuote] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['contracts', { organization_id: orgId }],
    queryFn: () => api.listContracts({ organization_id: orgId }),
    enabled: !!orgId,
  });
  const contracts = data?.data ?? [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['contracts'] });
    queryClient.invalidateQueries({ queryKey: ['customer-hub-summary', orgId] });
  };

  return (
    <HubPanel
      title={t('organization.tabContracts')}
      subtitle={t('organization.contractsSubtitle')}
      action={canWrite ? (
        <div className="flex flex-wrap gap-2">
          {acceptedQuotes.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => setShowFromQuote(true)}>
              <ScrollText size={16} aria-hidden="true" /> {t('contracts.fromQuote.create')}
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(true)}>
            <Plus size={16} aria-hidden="true" /> {t('contracts.new')}
          </button>
        </div>
      ) : undefined}
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <InlineError>{apiErrorMessage(error, t('contracts.loadError'))}</InlineError>
      ) : contracts.length === 0 ? (
        <HubSurface>
          <EmptyState icon={ScrollText} title={t('contracts.empty')} />
        </HubSurface>
      ) : (
        <HubSurface>
          <HubTable>
            <table className="table-modern">
              <thead>
                <tr>
                  <th>{t('contracts.fields.title')}</th>
                  <th>{t('common.status')}</th>
                  <th className="hidden md:table-cell text-end">{t('contracts.fields.period')}</th>
                  <th className="hidden lg:table-cell">{t('contracts.fields.quote')}</th>
                  <th className="hidden sm:table-cell text-end">{t('contracts.fields.version')}</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="relative isolate cursor-pointer">
                    <td className="md:max-w-xs">
                      <button
                        type="button"
                        onClick={() => onOpenContract(c.id)}
                        className="dt-link block max-w-full break-words md:truncate text-start font-medium text-surface-900 hover:text-primary-700 hover:underline underline-offset-2 rounded-xs"
                      >
                        {c.title}
                      </button>
                    </td>
                    <td><ContractStatusBadge status={c.status} /></td>
                    <td className="hidden md:table-cell text-end whitespace-nowrap tabular-nums text-surface-600">
                      {c.start_date || c.end_date ? `${fmtDate(c.start_date)} – ${fmtDate(c.end_date)}` : '—'}
                    </td>
                    <td className="hidden lg:table-cell whitespace-nowrap tabular-nums text-surface-600"><span dir="ltr">{c.quote_number ? `#${c.quote_number}` : '—'}</span></td>
                    <td className="hidden sm:table-cell text-end tabular-nums">{c.current_version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HubTable>
        </HubSurface>
      )}

      {showCreate && (
        <ContractFormModal
          orgId={orgId}
          onClose={() => setShowCreate(false)}
          onSaved={(c) => { setShowCreate(false); refresh(); onOpenContract(c.id); }}
        />
      )}
      {showFromQuote && (
        <CreateContractFromQuoteModal
          quotes={acceptedQuotes}
          onClose={() => setShowFromQuote(false)}
          onCreated={(c) => { setShowFromQuote(false); refresh(); onOpenContract(c.id); }}
        />
      )}
      {openContractId && (
        <ContractDetailModal
          contractId={openContractId}
          canWrite={canWrite}
          onClose={() => onOpenContract(null)}
          onChanged={refresh}
        />
      )}
    </HubPanel>
  );
}

/* ── Create / edit ───────────────────────────────────────────────────── */

export function ContractFormModal({
  orgId,
  contract,
  onClose,
  onSaved,
}: {
  orgId: string;
  /** Edit mode when given (draft contracts only). */
  contract?: Contract;
  onClose: () => void;
  onSaved: (c: Contract) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(contract?.title ?? '');
  const [terms, setTerms] = useState(contract?.terms ?? '');
  const [startDate, setStartDate] = useState(contract?.start_date?.slice(0, 10) ?? '');
  const [endDate, setEndDate] = useState(contract?.end_date?.slice(0, 10) ?? '');
  const [engagementId, setEngagementId] = useState(contract?.engagement_id ?? '');
  const [changeNote, setChangeNote] = useState('');

  const engagementsQuery = useQuery({
    queryKey: ['engagements', { organization_id: orgId }],
    queryFn: () => api.getEngagements({ organization_id: orgId, limit: 200 }),
  });
  const engagements = engagementsQuery.data?.data ?? [];

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        terms: terms.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
        engagement_id: engagementId || null,
      };
      return contract
        ? api.updateContract(contract.id, { ...body, change_note: changeNote.trim() || undefined })
        : api.createContract({ organization_id: orgId, ...body, engagement_id: engagementId || undefined });
    },
    onSuccess: (res) => { if (res.data) onSaved(res.data); },
  });

  const datesInvalid = !!startDate && !!endDate && endDate < startDate;

  return (
    <Modal open title={contract ? t('contracts.edit') : t('contracts.new')} onClose={onClose} sizeClass="max-w-xl"
      icon={<ScrollText size={18} className="text-accent-text" />}>
      <div className="space-y-4 max-h-[75vh] overflow-y-auto">
        <div>
          <label htmlFor="contract-f-1" className="label">{t('contracts.fields.title')} *</label>
          <input id="contract-f-1" className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <label htmlFor="contract-f-2" className="label">{t('contracts.fields.terms')}</label>
          <textarea id="contract-f-2" className="input min-h-40" rows={8} value={terms} onChange={(e) => setTerms(e.target.value)} />
          <p className="text-xs text-surface-500 mt-1">{t('contracts.termsHint')}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="contract-f-3" className="label">{t('contracts.fields.startDate')}</label>
            <DateInput id="contract-f-3" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label htmlFor="contract-f-4" className="label">{t('contracts.fields.endDate')}</label>
            <DateInput id="contract-f-4" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        {datesInvalid && <p className="text-xs text-danger-700">{t('contracts.datesInvalid')}</p>}
        {engagements.length > 0 && (
          <div>
            <label htmlFor="contract-f-5" className="label">{t('contracts.fields.engagement')}</label>
            <EntityPicker
              id="contract-f-5"
              emptyLabel={t('common.none')}
              options={engagements.map((e) => ({ value: e.id, label: `${e.child_name || e.id} · ${t(`engagementDetail.types.${e.type}`)}` }))}
              value={engagementId}
              onChange={setEngagementId}
            />
          </div>
        )}
        {contract && (
          <div>
            <label htmlFor="contract-f-6" className="label">{t('contracts.fields.changeNote')}</label>
            <input id="contract-f-6" className="input" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
            <p className="text-xs text-surface-500 mt-1">{t('contracts.newVersionHint')}</p>
          </div>
        )}
        {mutation.isError && <InlineError>{apiErrorMessage(mutation.error, t('contracts.saveError'))}</InlineError>}
        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={!title.trim() || datesInvalid || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ── Detail ──────────────────────────────────────────────────────────── */

function ContractDetailModal({
  contractId,
  canWrite,
  onClose,
  onChanged,
}: {
  contractId: string;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fmtDate = useDateFormatter();
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState<ContractStatus | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => api.getContract(contractId),
  });
  const contract: ContractDetail | undefined = data?.data;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
    onChanged();
  };

  const transitionMutation = useMutation({
    mutationFn: ({ status, reason }: { status: ContractStatus; reason: string }) =>
      api.transitionContract(contractId, status, reason || undefined),
    onSuccess: () => { setTarget(null); setError(null); refresh(); },
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteContract(contractId),
    onSuccess: () => { setConfirmDelete(false); onChanged(); onClose(); },
    onError: (err) => { setConfirmDelete(false); setError(apiErrorMessage(err, t('contracts.saveError'))); },
  });

  const allowed = contract ? CONTRACT_TRANSITIONS[contract.status] ?? [] : [];
  const versions = [...(contract?.versions ?? [])].sort((a, b) => b.version_number - a.version_number);

  return (
    <Modal open title={contract?.title ?? t('contracts.title')} onClose={onClose} sizeClass="max-w-2xl"
      icon={<ScrollText size={18} className="text-accent-text" />}>
      {isLoading ? (
        <Spinner />
      ) : isError || !contract ? (
        <InlineError>{apiErrorMessage(loadError, t('contracts.loadError'))}</InlineError>
      ) : (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto">
          <div className="flex flex-wrap items-center gap-2">
            <ContractStatusBadge status={contract.status} />
            <span className="text-xs text-surface-500">{t('contracts.versionLabel', { number: contract.current_version })}</span>
            {contract.quote_id && (
              <Link to={`/quotes/${contract.quote_id}`} closed="hide" className="text-xs text-accent-text hover:underline">
                {t('contracts.fromQuoteLink', { number: contract.quote_number ?? '' })}
              </Link>
            )}
          </div>

          <InlineError>{error}</InlineError>

          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Meta label={t('contracts.fields.startDate')} value={fmtDate(contract.start_date)} />
            <Meta label={t('contracts.fields.endDate')} value={fmtDate(contract.end_date)} />
            <Meta label={t('contracts.fields.signedAt')} value={fmtDate(contract.signed_at)} />
            <Meta label={t('contracts.fields.createdBy')} value={contract.created_by_name ?? '—'} />
            <Meta label={t('contracts.fields.createdAt')} value={fmtDate(contract.created_at)} />
            {contract.terminated_at && <Meta label={t('contracts.fields.terminatedAt')} value={fmtDate(contract.terminated_at)} />}
          </dl>
          {contract.status_reason && (
            <p className="text-sm text-surface-700 bg-surface-50 border border-surface-200 rounded-lg p-2">
              <span className="font-medium">{t('contracts.statusReason')}: </span>{contract.status_reason}
            </p>
          )}

          {contract.parties?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-surface-800 mb-1">{t('contracts.fields.parties')}</h3>
              <ul className="text-sm text-surface-700 space-y-0.5">
                {contract.parties.map((p, i) => (
                  <li key={`${p.role}-${i}`}><span className="text-surface-500">{t(`contracts.partyRole.${p.role}`, { defaultValue: p.role })}:</span> {p.name}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-surface-800 mb-1">{t('contracts.fields.terms')}</h3>
            {contract.terms ? (
              <div className="rounded-lg border border-surface-200 bg-surface-50 p-3 text-sm text-surface-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                {contract.terms}
              </div>
            ) : (
              <p className="text-sm text-surface-500">{t('contracts.noTerms')}</p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-surface-800 mb-1">{t('contracts.documents')}</h3>
            {contract.documents?.length ? (
              <ul className="space-y-1">
                {contract.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
                    <Link to={`/documents?org=${contract.organization_id}&doc=${d.id}`}
                      className="inline-flex items-center gap-1.5 text-accent-text hover:underline truncate">
                      <FileText size={14} /> {d.title}
                    </Link>
                    <span className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-surface-500">{t(`documents.status_${d.status}`, d.status)}</span>
                      <ClassificationBadge classification={d.classification as 'internal' | 'client_visible' | 'financial_sensitive'} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-surface-500">{t('contracts.noDocuments')}</p>
            )}
          </div>

          <div>
            <button type="button" className="text-sm font-semibold text-surface-800 inline-flex items-center gap-1"
              onClick={() => setShowVersions((v) => !v)} aria-expanded={showVersions}>
              <History size={16} /> {t('contracts.versions', { count: versions.length })}
              {showVersions ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showVersions && (
              <ol className="mt-2 divide-y divide-surface-100">
                {versions.map((v) => (
                  <li key={v.id} className="py-2 text-sm">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium">{t('contracts.versionLabel', { number: v.version_number })} · {v.title}</span>
                      <span className="text-xs text-surface-500">{fmtDate(v.created_at, 'dd/MM/yyyy HH:mm')}{v.created_by_name ? ` · ${v.created_by_name}` : ''}</span>
                    </div>
                    {v.change_note && <p className="text-xs text-surface-600 mt-0.5">{v.change_note}</p>}
                    <p className="text-xs text-surface-500 mt-0.5">
                      {fmtDate(v.start_date)} – {fmtDate(v.end_date)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {canWrite && (
            <div className="flex flex-wrap gap-2 border-t border-surface-200 pt-3">
              {allowed.map((s) => (
                <button key={s} type="button"
                  className={`btn text-sm ${s === 'terminated' ? 'bg-danger-50 text-danger-700 border border-danger-100 hover:bg-danger-100' : 'btn-secondary'}`}
                  onClick={() => { transitionMutation.reset(); setTarget(s); }}>
                  {t(`contracts.transition.${s}`)}
                </button>
              ))}
              {contract.status === 'draft' && (
                <>
                  <button type="button" className="btn btn-secondary text-sm" onClick={() => setEditing(true)}>
                    <Pencil size={14} /> {t('common.edit')}
                  </button>
                  <button type="button" className="btn text-sm bg-danger-50 text-danger-700 border border-danger-100 hover:bg-danger-100"
                    onClick={() => setConfirmDelete(true)}>
                    <Trash2 size={14} /> {t('common.delete')}
                  </button>
                </>
              )}
            </div>
          )}
          {canWrite && contract.status !== 'draft' && (
            <p className="text-xs text-surface-500">{t('contracts.editOnlyDraft')}</p>
          )}
        </div>
      )}

      {editing && contract && (
        <ContractFormModal
          orgId={contract.organization_id}
          contract={contract}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); refresh(); }}
        />
      )}
      {target && contract && (
        <ReasonModal
          title={t(`contracts.transition.${target}`)}
          description={t('contracts.transitionConfirm', { status: t(`contracts.status.${target}`) })}
          confirmLabel={t(`contracts.transition.${target}`)}
          reasonLabel={target === 'terminated' ? t('contracts.reason') : t('contracts.reasonOptional')}
          reasonRequired={target === 'terminated'}
          minLength={target === 'terminated' ? 5 : 0}
          danger={target === 'terminated'}
          loading={transitionMutation.isPending}
          error={transitionMutation.isError ? apiErrorMessage(transitionMutation.error, t('contracts.saveError')) : null}
          onConfirm={(reason) => transitionMutation.mutate({ status: target, reason })}
          onCancel={() => setTarget(null)}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title={t('common.delete')}
          description={t('contracts.deleteConfirm')}
          confirmLabel={t('common.delete')}
          danger
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-surface-500">{label}</dt>
      <dd className="text-surface-900">{value}</dd>
    </div>
  );
}
